import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { db } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import { createNotification } from '../../../utils/createNotification';
import { getPendingMemberDisplayName, isWaitlistedStatus, sortWaitlistMembers } from '../../../utils/waitlist';

function formatTimestamp(value) {
  const date = typeof value?.toDate === 'function' ? value.toDate() : value instanceof Date ? value : null;
  return date ? date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

function renumberWaitlist(members = []) {
  return members.map((member, index) => ({
    ...member,
    waitlistPriority: index + 1,
  }));
}

export default function EditWaitlistMembersForm({ isOpen, teamId, teamName, onClose, onSave }) {
  const [waitlistMembers, setWaitlistMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [releaseCount, setReleaseCount] = useState('1');
  const [joinRoutingEnabled, setJoinRoutingEnabled] = useState(false);
  const [routingSaving, setRoutingSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    getDoc(doc(db, 'teams', teamId))
      .then((teamSnap) => {
        if (cancelled) return;
        setJoinRoutingEnabled(teamSnap?.data()?.modules?.waitlist?.joinRoutingEnabled === true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load waitlist settings.');
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    setLoading(true);
    setError('');

    const unsub = safeOnSnapshot(
      collection(db, 'teams', teamId, 'pendingMembers'),
      (snap) => {
        const items = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((member) => isWaitlistedStatus(member.status));
        setWaitlistMembers(sortWaitlistMembers(items));
        setLoading(false);
      },
      (err) => {
        setError(err?.message || 'Failed to load the waitlist.');
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isOpen, teamId]);

  const orderedMembers = useMemo(() => renumberWaitlist(sortWaitlistMembers(waitlistMembers)), [waitlistMembers]);
  const maxReleaseCount = orderedMembers.length;

  const persistOrder = async (members) => {
    const batch = writeBatch(db);
    members.forEach((member, index) => {
      batch.update(doc(db, 'teams', teamId, 'pendingMembers', member.id), {
        waitlistPriority: index + 1,
      });
    });
    await batch.commit();
  };

  const moveMember = async (fromIndex, toIndex) => {
    if (saving || toIndex < 0 || toIndex >= orderedMembers.length) return;
    const next = [...orderedMembers];
    const [movedMember] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, movedMember);
    const renumbered = renumberWaitlist(next);

    setSaving(true);
    setError('');
    setWaitlistMembers(renumbered);
    try {
      await persistOrder(renumbered);
      onSave?.();
    } catch (err) {
      setError(err?.message || 'Could not update the waitlist order.');
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async () => {
    if (saving || maxReleaseCount === 0) return;
    const parsedCount = Number.parseInt(releaseCount, 10);
    const count = Number.isFinite(parsedCount) ? Math.max(1, Math.min(parsedCount, maxReleaseCount)) : 1;
    const releasingMembers = orderedMembers.slice(0, count);
    const remainingMembers = orderedMembers.slice(count);

    setSaving(true);
    setError('');
    try {
      const batch = writeBatch(db);

      releasingMembers.forEach((member) => {
        batch.update(doc(db, 'teams', teamId, 'pendingMembers', member.id), {
          status: 'approved',
          approvedAt: serverTimestamp(),
          waitlistPriority: null,
          waitlistedAt: null,
          releasedFromWaitlistAt: serverTimestamp(),
        });
      });

      remainingMembers.forEach((member, index) => {
        batch.update(doc(db, 'teams', teamId, 'pendingMembers', member.id), {
          waitlistPriority: index + 1,
        });
      });

      await batch.commit();
      await Promise.all(
        releasingMembers.map((member) =>
          createNotification({
            userId: member.id,
            message: `A place is now available in "${teamName}". Return to complete your membership payment.`,
            actionUrl: `/join/${teamId}`,
            teamId,
            notificationCategory: 'events',
            pushContext: teamName,
          })
        )
      );
      setReleaseCount('1');
      onSave?.();
    } catch (err) {
      setError(err?.message || 'Could not release waitlisted members.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleJoinRouting = async (nextValue) => {
    if (routingSaving) return;
    setRoutingSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        'modules.waitlist.joinRoutingEnabled': nextValue === true,
      });
      setJoinRoutingEnabled(nextValue === true);
    } catch (err) {
      setError(err?.message || 'Could not update waitlist entry settings.');
    } finally {
      setRoutingSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2 className="modal-title">Manage Waitlist</h2>
        {error && <p className="text-error">{error}</p>}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="modal-form space-y-6">
            <div className="form-toggle-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <span className="form-toggle-label" style={{ display: 'block', fontWeight: 600 }}>
                  {joinRoutingEnabled ? 'Waitlist active' : 'Waitlist inactive'}
                </span>
                <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                  {joinRoutingEnabled
                    ? 'Approved joiners are added to the waitlist before payment.'
                    : 'Waitlist inactive, toggle to activate the waitlist.'}
                </span>
                <div className="text-muted small" style={{ marginTop: '0.35rem' }}>
                  {maxReleaseCount} {maxReleaseCount === 1 ? 'person is' : 'people are'} currently waiting.
                </div>
              </div>
              <label className="toggle-switch" aria-label="Toggle waitlist active">
                <input
                  type="checkbox"
                  checked={joinRoutingEnabled}
                  onChange={(event) => handleToggleJoinRouting(event.target.checked)}
                  disabled={routingSaving}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 180 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>
                  Allow entry for top
                </label>
                <input
                  type="number"
                  min="1"
                  max={Math.max(1, maxReleaseCount)}
                  value={releaseCount}
                  onChange={(event) => setReleaseCount(event.target.value)}
                  className="form-input"
                  disabled={saving || maxReleaseCount === 0}
                />
              </div>

              <SubmitButton
                type="button"
                label={saving ? 'Releasing…' : 'Release to Payment'}
                onClick={handleRelease}
                disabled={saving || maxReleaseCount === 0}
              />
            </div>

            {maxReleaseCount === 0 ? (
              <p className="text-muted small" style={{ marginBottom: 0 }}>
                Nobody is currently on the waitlist.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {orderedMembers.map((member, index) => {
                  const displayName = getPendingMemberDisplayName(member);
                  const joinedWaitlistAt = formatTimestamp(member.waitlistedAt || member.createdAt);
                  const targetCount = Array.isArray(member.membershipTargetUserIds) ? member.membershipTargetUserIds.length : 1;
                  return (
                    <div
                      key={member.id}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '0.85rem',
                        display: 'grid',
                        gap: '0.5rem',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <div>
                          <strong>#{index + 1} {displayName}</strong>
                          <div className="text-muted small">
                            {targetCount > 1 ? `Family request (${targetCount} members)` : 'Individual request'}
                          </div>
                          {joinedWaitlistAt && (
                            <div className="text-muted small">
                              Waitlisted {joinedWaitlistAt}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className="button button-secondary"
                            onClick={() => moveMember(index, index - 1)}
                            disabled={saving || index === 0}
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            className="button button-secondary"
                            onClick={() => moveMember(index, index + 1)}
                            disabled={saving || index === orderedMembers.length - 1}
                          >
                            Down
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="form-actions">
              <SubmitButton
                type="button"
                onClick={onClose}
                label="Close"
                variant="ghost"
                size="sm"
                disabled={saving}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
